import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDebounce } from "@/hooks/useDebounce";
import { clientsApi } from "@/lib/api";
import { cn } from "@/lib/utils";

interface CouponClientPickerProps {
  value: string[];
  onChange: (codes: string[]) => void;
  /** Nomes fantasia já conhecidos (ex.: cliente de um cupom em edição), por código. */
  initialNames?: Record<string, string>;
  /** Oculta o texto de ajuda (modo somente leitura). */
  hideHint?: boolean;
  /** Cliente obrigatório: sem a opção "Qualquer cliente". */
  required?: boolean;
}

/**
 * Seleção do cliente do cupom em uma lista única: abre a lista, busca por nome/código e escolhe UM cliente (ou
 * "Qualquer cliente"). O valor segue como lista de códigos (no máximo 1), que é o formato da API.
 */
export function CouponClientPicker({ value, onChange, initialNames, hideHint = false, required = false }: CouponClientPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pickedNames, setPickedNames] = useState<Record<string, string>>({});
  const names = { ...(initialNames ?? {}), ...pickedNames };
  const debouncedQuery = useDebounce(query.trim(), 350);

  const searchQuery = useQuery({
    queryKey: ["coupon-client-search", debouncedQuery],
    queryFn: () => clientsApi.search(debouncedQuery),
    enabled: open && debouncedQuery.length >= 2,
    staleTime: 30_000,
  });

  // A busca devolve os campos em camelCase (clientCodeCnx, clientFantasyName...); aceita também o formato legado.
  const results = (searchQuery.data?.data ?? [])
    .map((raw) => {
      const client = raw as unknown as Record<string, string | null | undefined>;
      const code = (client.clientCodeCnx ?? client.codigo_cnx ?? "").toString().trim().toUpperCase();
      const name = (client.clientFantasyName ?? client.clientCompanyName ?? client.nome_cliente ?? "").toString().trim();
      const company = (client.clientCompanyName ?? "").toString().trim();
      return { code, name, company: company && company !== name ? company : "" };
    })
    .filter((client) => client.code);

  const selectedCode = value[0] ?? null;
  const selectedName = selectedCode ? names[selectedCode] : "";

  const choose = (code: string | null, name = "") => {
    if (code) {
      setPickedNames((current) => ({ ...current, [code]: name }));
      onChange([code]);
    } else {
      onChange([]);
    }
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="grid gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            {selectedCode ? (
              <span className="flex min-w-0 items-center gap-2">
                <span className="shrink-0 whitespace-nowrap font-mono text-xs">{selectedCode}</span>
                <span className="truncate">{selectedName || "—"}</span>
              </span>
            ) : (
              <span className="text-muted-foreground">{required ? "Selecione o cliente" : "Qualquer cliente"}</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          onWheel={(event) => event.stopPropagation()}
        >
          <Command shouldFilter={false}>
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder="Buscar por nome ou código (mín. 2 letras)"
            />
            <CommandList>
              {!required && (
                <CommandGroup>
                  <CommandItem value="__any__" onSelect={() => choose(null)}>
                    <Check className={cn("mr-2 h-4 w-4", selectedCode ? "opacity-0" : "opacity-100")} />
                    Qualquer cliente
                  </CommandItem>
                </CommandGroup>
              )}
              {debouncedQuery.length >= 2 && (
                <CommandGroup heading="Clientes">
                  {searchQuery.isFetching ? (
                    <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> Buscando clientes...
                    </div>
                  ) : results.length === 0 ? (
                    <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
                  ) : (
                    results.map((client) => (
                      <CommandItem
                        key={client.code}
                        value={client.code}
                        onSelect={() => choose(client.code, client.name)}
                      >
                        <Check
                          className={cn("mr-2 h-4 w-4 shrink-0", selectedCode === client.code ? "opacity-100" : "opacity-0")}
                        />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">{client.name || "—"}</div>
                          <div className="truncate text-xs text-muted-foreground">
                            <span className="whitespace-nowrap font-mono">{client.code}</span>
                            {client.company && ` · ${client.company}`}
                          </div>
                        </div>
                      </CommandItem>
                    ))
                  )}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {!hideHint && (
        <p className="text-xs text-muted-foreground">
          {selectedCode
            ? "Cupom exclusivo deste cliente."
            : required ? "Selecione o cliente exclusivo deste cupom." : "Sem cliente selecionado = vale para qualquer cliente. Selecione no máximo 1."}
        </p>
      )}
    </div>
  );
}
